/**
 * Dashboard refresh scheduler (S3) — pure timer logic, no execution deps.
 * It NEVER runs queries: the only outlet is `refetchTile`, which triggers the
 * TanStack Query refetch (the single fetch path via `useTileResult` →
 * `startTileExecution`). Semantics per plan: interval ticks pause while the
 * dashboard is ineligible (background-budget alert tiles excepted), an
 * eligibility change triggers an immediate staggered catch-up (250ms/tile,
 * per-connection cap), a tick that would overlap a still-running execution
 * is skipped (not queued), and refresh-all coalesces already-queued runs.
 *
 * Both gates are re-checked when a queued run is DISPATCHED, not only when it
 * was queued: the stagger and the per-connection cap can hold a run in the
 * queue for seconds, and in that window the dashboard can be hidden or the
 * tile's manual "Refresh now" can start. A scheduled run dequeued while its
 * dashboard is ineligible is dropped and marked missed (the next eligibility
 * change catches it up); any run dequeued while its tile is still running is
 * skipped exactly like an overlapping tick. Only an explicit refresh-all keeps
 * dispatching on a hidden dashboard — the user asked for it.
 */
import { isQueryBackedTile, type DashboardTile } from '../types/dashboardTypes';

export const REFRESH_STAGGER_MS = 250;

/**
 * Composite scheduler identity (A6). A playlist registers its shown dashboard
 * AND its on-deck dashboard into the SAME scheduler instance, and dashboards
 * duplicated from a shared template can carry identical tile ids — so `tile.id`
 * alone is not unique across the scheduler, and keying on it silently clobbers
 * one dashboard's tile with the other's. Dashboard ids are validated to
 * exclude whitespace, so the space separator below keeps this key unambiguous
 * even when a tile id itself contains spaces.
 */
export const schedulerEntryKey = (dashboardId: string, tileId: string): string =>
  `${dashboardId}\u0000${tileId}`;

export type RefreshSchedulerOpts = {
  /** Triggers the TanStack Query refetch — the scheduler's ONLY outlet. */
  refetchTile(dashboardId: string, tileId: string): void;
  /** Per-connection concurrent dispatch cap (spec: 2). */
  maxConcurrentPerConnection: number;
  /** Visible tab OR playlist on-deck (S13) OR background-budget alert tile (S12). */
  isDashboardEligible(dashboardId: string): boolean;
  /** Push signal — staggered catch-up runs on change. Returns unsubscribe. */
  onEligibilityChange(cb: () => void): () => void;
  backgroundBudget: { maxTiles: number; minIntervalSeconds: number };
  /**
   * Newest runtime knowledge (wired from the runtime store). A due tick for a
   * still-running tile is skipped, and the per-connection cap counts tiles
   * that still report running. Optional: without it, dispatched tiles are
   * assumed done by the next pump cycle.
   */
  isTileRunning?(dashboardId: string, tileId: string): boolean;
};

type Entry = {
  /** Composite `dashboardId\0tileId` — unique across playlist co-registration. */
  key: string;
  dashboardId: string;
  tile: Extract<DashboardTile, { connectionId: string }>;
  timer: ReturnType<typeof setTimeout> | null;
  missedWhilePaused: boolean;
  lastRunAt: number | null;
};

/** One queued run. `explicit` marks a refresh-all request, which dispatches
 * regardless of eligibility; a scheduled run (tick or catch-up) re-checks the
 * pause gate when it is dequeued. A refresh-all that lands on an already
 * queued scheduled run upgrades it. */
type QueuedRun = { key: string; explicit: boolean };

export function createRefreshScheduler(opts: RefreshSchedulerOpts) {
  // All internal collections are keyed by the composite entry key (A6), never
  // by tile.id alone — see schedulerEntryKey.
  const entries = new Map<string, Entry>();
  const backgroundOrder: string[] = [];
  const queue: QueuedRun[] = [];
  /** entry key → pump cycle at dispatch; pruned once past-age AND not running. */
  const inFlight = new Map<string, number>();
  let pumpTimer: ReturnType<typeof setTimeout> | null = null;
  let pumpCycle = 0;
  let disposed = false;

  const isBackgroundPrivileged = (entry: Entry): boolean =>
    entry.tile.tileType === 'stat' &&
    entry.tile.alertRule?.background === true &&
    backgroundOrder.indexOf(entry.key) > -1 &&
    backgroundOrder.indexOf(entry.key) < opts.backgroundBudget.maxTiles;

  const pruneInFlight = () => {
    for (const [key, dispatchedAt] of [...inFlight]) {
      if (pumpCycle - dispatchedAt < 1) continue; // grace: store may not report yet
      const entry = entries.get(key);
      const stillRunning =
        entry !== undefined && opts.isTileRunning?.(entry.dashboardId, entry.tile.id) === true;
      if (!stillRunning) inFlight.delete(key);
    }
  };

  const runningOnConnection = (connectionId: string): number => {
    let count = 0;
    for (const key of inFlight.keys()) {
      if (entries.get(key)?.tile.connectionId === connectionId) count++;
    }
    return count;
  };

  const schedulePump = (delayMs: number) => {
    if (pumpTimer !== null || disposed) return;
    pumpTimer = setTimeout(pump, delayMs);
  };

  const isPaused = (entry: Entry): boolean =>
    !opts.isDashboardEligible(entry.dashboardId) && !isBackgroundPrivileged(entry);

  const isRunning = (entry: Entry): boolean =>
    opts.isTileRunning?.(entry.dashboardId, entry.tile.id) === true;

  /**
   * Whether a queued run may still go out NOW. `dispatch` sends it; `drop`
   * removes it without sending (the pause or overlap gate closed while it
   * waited); `hold` keeps it queued for a later cycle (per-connection cap).
   */
  const dispatchVerdict = (run: QueuedRun, entry: Entry): 'dispatch' | 'drop' | 'hold' => {
    if (!run.explicit && isPaused(entry)) {
      entry.missedWhilePaused = true; // the eligibility catch-up re-queues it
      return 'drop';
    }
    if (isRunning(entry)) return 'drop'; // overlap → skipped, like a tick
    return runningOnConnection(entry.tile.connectionId) < opts.maxConcurrentPerConnection
      ? 'dispatch'
      : 'hold';
  };

  const pump = () => {
    pumpTimer = null;
    if (disposed) return;
    pumpCycle++;
    pruneInFlight();
    let index = 0;
    while (index < queue.length) {
      const run = queue[index];
      const entry = entries.get(run.key);
      const verdict = entry === undefined ? 'drop' : dispatchVerdict(run, entry);
      if (verdict === 'hold') {
        index++;
        continue;
      }
      queue.splice(index, 1);
      if (verdict === 'dispatch' && entry !== undefined) {
        inFlight.set(run.key, pumpCycle);
        opts.refetchTile(entry.dashboardId, entry.tile.id);
        break; // one dispatch per cycle — the stagger
      }
    }
    if (queue.length > 0) schedulePump(REFRESH_STAGGER_MS);
  };

  const enqueue = (entry: Entry, explicit = false) => {
    const queued = queue.find((run) => run.key === entry.key);
    if (queued) {
      queued.explicit ||= explicit; // coalesce; refresh-all upgrades a scheduled run
      return;
    }
    entry.lastRunAt = Date.now();
    queue.push({ key: entry.key, explicit });
    schedulePump(0);
  };

  const scheduleTick = (entry: Entry, delayMs: number) => {
    entry.timer = setTimeout(() => onTick(entry), delayMs);
  };

  const onTick = (entry: Entry) => {
    if (disposed || entries.get(entry.key) !== entry) return;
    if (entry.tile.refresh.mode !== 'interval') return;
    const intervalMs = entry.tile.refresh.seconds * 1000;
    const eligible = opts.isDashboardEligible(entry.dashboardId);
    if (!eligible) {
      if (!isBackgroundPrivileged(entry)) {
        entry.missedWhilePaused = true;
        scheduleTick(entry, intervalMs);
        return;
      }
      // Background tiles keep firing, but never faster than the budget floor.
      const minMs = opts.backgroundBudget.minIntervalSeconds * 1000;
      const sinceLast = entry.lastRunAt === null ? Infinity : Date.now() - entry.lastRunAt;
      if (sinceLast < minMs) {
        scheduleTick(entry, minMs - sinceLast);
        return;
      }
    }
    if (isRunning(entry)) {
      scheduleTick(entry, intervalMs); // overlap → skip this tick, do NOT queue
      return;
    }
    enqueue(entry);
    scheduleTick(entry, intervalMs);
  };

  const unregister = (dashboardId: string, tileId: string) => {
    const key = schedulerEntryKey(dashboardId, tileId);
    const entry = entries.get(key);
    if (entry?.timer) clearTimeout(entry.timer);
    entries.delete(key);
    inFlight.delete(key);
    const qi = queue.findIndex((run) => run.key === key);
    if (qi !== -1) queue.splice(qi, 1);
    const bi = backgroundOrder.indexOf(key);
    if (bi !== -1) backgroundOrder.splice(bi, 1);
  };

  const unsubscribe = opts.onEligibilityChange(() => {
    if (disposed) return;
    for (const entry of entries.values()) {
      if (!entry.missedWhilePaused) continue;
      if (!opts.isDashboardEligible(entry.dashboardId)) continue;
      entry.missedWhilePaused = false;
      enqueue(entry); // catch-up — staggered by the pump
    }
  });

  return {
    register(dashboardId: string, tile: DashboardTile): void {
      if (disposed || !isQueryBackedTile(tile)) return;
      unregister(dashboardId, tile.id); // re-register = reconfigure
      const key = schedulerEntryKey(dashboardId, tile.id);
      const entry: Entry = {
        key,
        dashboardId,
        tile,
        timer: null,
        missedWhilePaused: false,
        lastRunAt: null,
      };
      entries.set(key, entry);
      if (tile.tileType === 'stat' && tile.alertRule?.background === true) {
        backgroundOrder.push(key);
      }
      if (tile.refresh.mode === 'interval') {
        scheduleTick(entry, tile.refresh.seconds * 1000);
      }
    },

    unregister,

    /** Explicit action — dispatches regardless of eligibility (a tile whose
     * run is still in flight when its turn comes is skipped); coalesced. */
    refreshAll(dashboardId: string): void {
      if (disposed) return;
      for (const entry of entries.values()) {
        if (entry.dashboardId === dashboardId) enqueue(entry, true);
      }
    },

    dispose(): void {
      disposed = true;
      unsubscribe();
      for (const entry of entries.values()) {
        if (entry.timer) clearTimeout(entry.timer);
      }
      entries.clear();
      queue.length = 0;
      inFlight.clear();
      if (pumpTimer) clearTimeout(pumpTimer);
      pumpTimer = null;
    },
  };
}

export type RefreshScheduler = ReturnType<typeof createRefreshScheduler>;
